import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import StarIcon from "@material-ui/icons/Star";
import DraftsIcon from "@material-ui/icons/Drafts";
import PersonIcon from "@material-ui/icons/Person";
import ReplyIcon from "@material-ui/icons/Reply";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	getSupportMessagesAdmin,
	getSupportMessageSendersAdmin,
	markSupportMessageRead,
	replySupportMessage,
} from "../../../../api/support-message";
import SupportReplyEditor from "./SupportReplyEditor";
import "./SupportReplyEditor.css";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { shallowEqual, useSelector } from "react-redux";

function formatDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
	} catch (e) {
		return "—";
	}
}

function defaultReplySubject(originalSubject) {
	const s = String(originalSubject || "").trim();
	if (!s) return "Respuesta de soporte Evenely";
	if (/^re:\s/i.test(s)) return s;
	return `Re: ${s}`;
}

function sortSupportMessages(list) {
	return [...list].sort((a, b) => {
		const priA = a.prioritySupport ? 1 : 0;
		const priB = b.prioritySupport ? 1 : 0;
		if (priB !== priA) return priB - priA;
		return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
	});
}

function senderLabel(sender) {
	if (!sender) return "";
	const name = String(sender.fullName || "").trim();
	const email = String(sender.email || "").trim();
	if (name && email) return `${name} (${email})`;
	return name || email || "Usuario";
}

function SupportMessagesTable({
	rows,
	emptyMessage,
	markingId,
	onOpenDetail,
	onMarkRead,
	variant = "unread",
}) {
	return (
		<TableContainer component={Paper} variant="outlined">
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Prioridad</TableCell>
						<TableCell>Fecha</TableCell>
						<TableCell>Usuario</TableCell>
						<TableCell>Plan (rol)</TableCell>
						<TableCell>Asunto</TableCell>
						<TableCell>Estado</TableCell>
						<TableCell align="right">Acciones</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={7} align="center">
								{emptyMessage}
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => {
							const pri = Boolean(row.prioritySupport);
							const unread = !row.readByAdmin;
							const replied = Boolean(row.repliedAt);
							return (
								<TableRow
									key={row._id}
									style={{
										background:
											variant === "unread" && pri
												? "rgba(254, 243, 199, 0.55)"
												: pri
													? "rgba(254, 252, 232, 0.2)"
													: undefined,
										fontWeight: variant === "unread" ? 600 : 400,
									}}
								>
									<TableCell>
										{pri ? (
											<Chip
												size="small"
												icon={<StarIcon />}
												label="Prioritario"
												style={{ background: "#fbbf24", color: "#422006", fontWeight: 800 }}
											/>
										) : (
											<Chip size="small" label="Estándar" />
										)}
									</TableCell>
									<TableCell>{formatDate(row.createdAt)}</TableCell>
									<TableCell>
										<div>{row.fullName || "—"}</div>
										<div style={{ fontSize: 12, color: "#64748b" }}>{row.email || "—"}</div>
									</TableCell>
									<TableCell>{row.roleAtSend || "—"}</TableCell>
									<TableCell
										style={{
											maxWidth: 220,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{row.subject || "—"}
									</TableCell>
									<TableCell>
										{unread ? (
											<Chip size="small" label="Sin leer" color="secondary" />
										) : replied ? (
											<Chip
												size="small"
												label="Respondido"
												style={{ background: "#dcfce7", color: "#166534" }}
											/>
										) : (
											<Chip size="small" label="Leído" />
										)}
									</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => onOpenDetail(row)}>
											Gestionar
										</Button>
										{unread ? (
											<Button
												size="small"
												color="primary"
												startIcon={
													markingId === String(row._id) ? (
														<CircularProgress size={16} />
													) : (
														<DraftsIcon />
													)
												}
												disabled={markingId === String(row._id)}
												onClick={() => onMarkRead(row)}
											>
												Leído
											</Button>
										) : null}
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

export default function SupportMessagesPage() {
	const history = useHistory();
	const location = useLocation();
	const authUser = useSelector((s) => s.authentication?.user, shallowEqual);
	const isAdmin = String(authUser?.role || "").toLowerCase() === "admin";
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [senders, setSenders] = useState([]);
	const [replyFilter, setReplyFilter] = useState("");
	const [userFilter, setUserFilter] = useState(null);
	const [detail, setDetail] = useState(null);
	const [markingId, setMarkingId] = useState("");
	const [replySubject, setReplySubject] = useState("");
	const [replyBody, setReplyBody] = useState("");
	const [replySending, setReplySending] = useState(false);
	const urlUserAppliedRef = useRef(false);

	const load = useCallback(async () => {
		if (!isAdmin) return;
		setLoading(true);
		try {
			const res = await getSupportMessagesAdmin({
				limit: 300,
				replyStatus: replyFilter || undefined,
				userId: userFilter?.userId || undefined,
			});
			if (res.status === 200 && Array.isArray(res.data)) setRows(res.data);
			else setRows([]);
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar los mensajes de soporte." });
		} finally {
			setLoading(false);
		}
	}, [isAdmin, replyFilter, userFilter]);

	useEffect(() => {
		if (!isAdmin) return;
		let cancelled = false;
		getSupportMessageSendersAdmin()
			.then((res) => {
				if (cancelled) return;
				if (res?.status === 200 && Array.isArray(res.data)) setSenders(res.data);
				else setSenders([]);
			})
			.catch(() => {
				if (!cancelled) setSenders([]);
			});
		return () => {
			cancelled = true;
		};
	}, [isAdmin]);

	useEffect(() => {
		if (!isAdmin || senders.length === 0 || urlUserAppliedRef.current) return;
		const params = new URLSearchParams(location.search);
		const userId = String(params.get("userId") || "").trim();
		urlUserAppliedRef.current = true;
		if (!userId) return;
		const match = senders.find((s) => String(s.userId) === userId);
		if (match) setUserFilter(match);
	}, [isAdmin, senders, location.search]);

	useEffect(() => {
		if (!isAdmin || !urlUserAppliedRef.current) return;
		const params = new URLSearchParams(location.search);
		const currentUserId = String(params.get("userId") || "");
		const nextUserId = userFilter?.userId ? String(userFilter.userId) : "";
		if (currentUserId === nextUserId) return;
		if (nextUserId) params.set("userId", nextUserId);
		else params.delete("userId");
		const qs = params.toString();
		history.replace(qs ? `/support-messages?${qs}` : "/support-messages");
	}, [isAdmin, userFilter, history, location.search]);

	const hasActiveFilters = Boolean(replyFilter || userFilter);

	const clearFilters = () => {
		setReplyFilter("");
		setUserFilter(null);
	};

	useEffect(() => {
		load();
	}, [load]);

	const openDetail = useCallback((row) => {
		if (!row) return;
		setDetail(row);
		setReplySubject(defaultReplySubject(row.subject));
		setReplyBody("");
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const messageId = String(params.get("message") || "").trim();
		if (!messageId || rows.length === 0) return;
		const row = rows.find((r) => String(r._id) === messageId);
		if (row) openDetail(row);
	}, [location.search, rows, openDetail]);

	const closeDetail = () => {
		setDetail(null);
		setReplySubject("");
		setReplyBody("");
		if (location.search) history.replace("/support-messages");
	};

	const markRead = async (row) => {
		if (!row?._id || row.readByAdmin) return;
		setMarkingId(String(row._id));
		try {
			const res = await markSupportMessageRead(row._id);
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(row._id) ? res.data : r)));
				if (detail && String(detail._id) === String(row._id)) setDetail(res.data);
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo marcar como leído." });
		} finally {
			setMarkingId("");
		}
	};

	const openPortalUser = (row) => {
		const userId = String(row?.userId || "").trim();
		if (!userId) return;
		history.push(`/portal-users?userId=${encodeURIComponent(userId)}`);
	};

	const sendReply = async () => {
		if (!detail?._id || replySending) return;
		const subject = String(replySubject || "").trim();
		const body = String(replyBody || "").trim();
		const plain = body.replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
		if (subject.length < 2) {
			alertError({ customMessage: "Escribe un asunto para la respuesta." });
			return;
		}
		if (plain.length < 2) {
			alertError({ customMessage: "Escribe el mensaje de respuesta." });
			return;
		}
		setReplySending(true);
		try {
			const res = await replySupportMessage(detail._id, { subject, body });
			const updated = res.data?.data || res.data;
			if (updated) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(detail._id) ? updated : r)));
				setDetail(updated);
			}
			setReplyBody("");
			alertSuccess("Correo enviado al usuario con diseño Evenely.");
		} catch (error) {
			alertError({ error, customMessage: "No se pudo enviar la respuesta." });
		} finally {
			setReplySending(false);
		}
	};

	const unreadRows = useMemo(
		() => sortSupportMessages(rows.filter((r) => !r.readByAdmin)),
		[rows]
	);
	const readRows = useMemo(
		() => sortSupportMessages(rows.filter((r) => r.readByAdmin)),
		[rows]
	);
	const priorityUnread = useMemo(
		() => unreadRows.filter((r) => r.prioritySupport).length,
		[unreadRows]
	);
	const standardUnread = useMemo(
		() => unreadRows.filter((r) => !r.prioritySupport).length,
		[unreadRows]
	);

	if (!isAdmin) {
		return (
			<Card>
				<CardHeader title="Mensajes de soporte" />
				<CardBody>
					<Typography color="textSecondary">Solo los administradores pueden ver esta sección.</Typography>
				</CardBody>
			</Card>
		);
	}

	return (
		<>
			<Card>
				<CardHeader title="Mensajes de soporte" />
				<CardBody>
					<Button variant="outlined" size="small" onClick={load} disabled={loading} style={{ marginBottom: 16 }}>
						Actualizar
					</Button>

					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 12,
							alignItems: "center",
							marginBottom: 20,
						}}
					>
						<FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
							<InputLabel id="support-reply-filter-label">Respuesta</InputLabel>
							<Select
								labelId="support-reply-filter-label"
								label="Respuesta"
								value={replyFilter}
								onChange={(e) => setReplyFilter(e.target.value)}
							>
								<MenuItem value="">Todos</MenuItem>
								<MenuItem value="replied">Contestados</MenuItem>
								<MenuItem value="unreplied">Sin contestar</MenuItem>
							</Select>
						</FormControl>

						<Autocomplete
							size="small"
							options={senders}
							value={userFilter}
							onChange={(_, value) => setUserFilter(value)}
							getOptionLabel={senderLabel}
							getOptionSelected={(option, value) =>
								String(option?.userId || "") === String(value?.userId || "")
							}
							renderOption={(option) => (
								<div>
									<div style={{ fontWeight: 600 }}>{senderLabel(option)}</div>
									<div style={{ fontSize: 12, color: "#64748b" }}>
										{option.messageCount || 0} mensaje{(option.messageCount || 0) === 1 ? "" : "s"}
									</div>
								</div>
							)}
							renderInput={(params) => (
								<TextField {...params} label="Usuario" variant="outlined" placeholder="Buscar por nombre o email" />
							)}
							style={{ minWidth: 280, flex: "1 1 280px", maxWidth: 420 }}
							clearOnEscape
						/>

						{hasActiveFilters ? (
							<Button size="small" onClick={clearFilters}>
								Quitar filtros
							</Button>
						) : null}
					</div>

					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<>
							<div style={{ marginBottom: 28 }}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										marginBottom: 12,
									}}
								>
									<Typography variant="h6" style={{ fontWeight: 800, margin: 0 }}>
										Sin leer
									</Typography>
									<Chip size="small" label={unreadRows.length} color="secondary" />
									{priorityUnread > 0 ? (
										<Chip
											size="small"
											icon={<StarIcon />}
											label={`${priorityUnread} prioritarios`}
											style={{ background: "#fef3c7", fontWeight: 700 }}
										/>
									) : null}
									{standardUnread > 0 ? (
										<Chip size="small" label={`${standardUnread} estándar`} />
									) : null}
								</div>
								<Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
									Ordenados por prioridad y fecha (más recientes primero).
								</Typography>
								<SupportMessagesTable
									rows={unreadRows}
									emptyMessage={
										hasActiveFilters
											? "No hay mensajes sin leer con estos filtros."
											: "No hay mensajes sin leer."
									}
									markingId={markingId}
									onOpenDetail={openDetail}
									onMarkRead={markRead}
									variant="unread"
								/>
							</div>

							<div>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										marginBottom: 12,
									}}
								>
									<Typography variant="h6" style={{ fontWeight: 800, margin: 0 }}>
										Leídos
									</Typography>
									<Chip size="small" label={readRows.length} />
								</div>
								<Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
									Ordenados por prioridad y fecha (más recientes primero).
								</Typography>
								<SupportMessagesTable
									rows={readRows}
									emptyMessage={
										hasActiveFilters
											? "No hay mensajes leídos con estos filtros."
											: "No hay mensajes leídos."
									}
									markingId={markingId}
									onOpenDetail={openDetail}
									onMarkRead={markRead}
									variant="read"
								/>
							</div>
						</>
					)}
				</CardBody>
			</Card>

			<Dialog open={Boolean(detail)} onClose={closeDetail} maxWidth="lg" fullWidth>
				<DialogTitle>
					{detail?.subject || "Mensaje"}
					{detail?.prioritySupport ? (
						<Chip size="small" icon={<StarIcon />} label="Soporte prioritario" style={{ marginLeft: 12, background: "#fbbf24" }} />
					) : null}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						{detail?.fullName} · {detail?.email} · {formatDate(detail?.createdAt)}
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Plan al enviar: {detail?.roleAtSend || "—"}
					</Typography>

					<div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "16px 0 20px" }}>
						{detail?.userId ? (
							<Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<PersonIcon />}
								onClick={() => openPortalUser(detail)}
							>
								Gestionar usuario
							</Button>
						) : null}
						{detail && !detail.readByAdmin ? (
							<Button variant="outlined" size="small" onClick={() => markRead(detail)}>
								Marcar como leído
							</Button>
						) : null}
					</div>

					<Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>
						Mensaje del usuario
					</Typography>
					<Paper variant="outlined" style={{ padding: "12px 14px", background: "#faf5ff", marginBottom: 20 }}>
						<Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
							{detail?.body || ""}
						</Typography>
					</Paper>

					{detail?.repliedAt ? (
						<>
							<Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>
								Última respuesta enviada · {formatDate(detail.repliedAt)}
							</Typography>
							<Paper variant="outlined" style={{ padding: "12px 14px", marginBottom: 20 }}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									{detail.lastReplySubject || "—"}
								</Typography>
								{detail.lastReplyBodyHtml ? (
									<div
										className="support-reply-preview"
										dangerouslySetInnerHTML={{ __html: detail.lastReplyBodyHtml }}
									/>
								) : (
									<Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
										{detail.lastReplyBody || ""}
									</Typography>
								)}
							</Paper>
						</>
					) : null}

					<Divider style={{ margin: "8px 0 20px" }} />

					<Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: 12 }}>
						Responder por correo
					</Typography>
					<Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
						Se enviará a <strong>{detail?.email}</strong> con el diseño Evenely de soporte. Puedes usar formato, listas, enlaces e imágenes.
					</Typography>

					<TextField
						fullWidth
						label="Asunto"
						variant="outlined"
						size="small"
						value={replySubject}
						onChange={(e) => setReplySubject(e.target.value.slice(0, 280))}
						disabled={replySending}
						style={{ marginBottom: 16 }}
					/>
					<SupportReplyEditor
						value={replyBody}
						onChange={setReplyBody}
						disabled={replySending}
						placeholder="Redacta la respuesta. Puedes insertar imágenes desde el icono de imagen de la barra."
					/>

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
						<Button onClick={closeDetail} disabled={replySending}>
							Cerrar
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={replySending ? <CircularProgress size={18} color="inherit" /> : <ReplyIcon />}
							disabled={replySending}
							onClick={sendReply}
						>
							Enviar correo
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
